import { useRenderedBootHumanWait } from '#~/routes/boot-overlay/boot-budget-view.js';
/* eslint-disable jsdoc/require-jsdoc */

/* eslint-disable arrow-body-style */

/* eslint-disable unicorn/consistent-function-scoping */

/* eslint-disable unicorn/prevent-abbreviations */
import { toHexFromId, toIdFromHex } from '#~/api/index.js';
import { useAuth0 } from '#~/auth/index.js';
import { nats } from '#~/nats/index.js';
import { Paths } from '#~/routes/index.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { Account } from '../scaffold/header/account/index.js';
import { InviteError } from './components/index.js';
import { Loader } from '@studio/design-system/loader';
import { Centered } from '@studio/design-system/overlay';
import { AgentRegistryRedeemInvite } from '@tryiris-ai/mailroom-helper/agentRegistry';
import { ensureDefaultOrgAndRoom } from '#~/account/bootstrap.js';
import { clsx } from 'cn';
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { twMerge } from 'cn';
import { extractOrganizationData } from '#~/auth/utils.js';
import { useConfig } from '#~/config/hooks.js';

let isRedeeming = false;

export const PostRedeem = (): ReactElement => {
  const params = useParams();

  const config = useConfig();

  // BootOverlay dismissal for this route is owned by <BootReadyLayout> (router.tsx),
  // which publishes boot.isRootReady on mount; this route no longer does (ENG-4186).

  const inviteIdB64 = params.inviteId!;
  const redemptionTokenB64 = params.redemptionToken!;
  const { track } = useAnalytics();

  const unurlifyBase64 = (str: string): string => {
    // Reverse of urlifyBase64 - converts URL-safe base64 back to standard base64
    // - Replaces '-' with '+'
    // - Replaces '_' with '/'
    // - Adds padding '=' if needed
    const base64 = str.replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    return base64 + padding;
  };

  const inviteId = toIdFromHex(unurlifyBase64(inviteIdB64))!;
  const redemptionToken = toIdFromHex(unurlifyBase64(redemptionTokenB64))!;
  const [errorCode, setErrorCode] = useState<number | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [inviteeObscuredEmail, setEmail] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const { getAccessTokenSilently, user: auth0User } = useAuth0();

  useEffect(() => {
    if (isRedeeming) {
      return;
    }

    isRedeeming = true;

    const fn = async () => {
      const token = await getAccessTokenSilently();

      try {
        const resp = await AgentRegistryRedeemInvite.request(nats.cloudConnection(), {
          token,
          id: inviteId,
          redemptionToken
        });
        if (resp.invite?.error?.code) {
          setErrorCode(resp.invite.error.code);
          setErrorMessage(resp.invite.error.message);
          setEmail(resp.invite?.inviteeObscuredEmail);
          setLoading(false);
        } else if (resp.error) {
          setErrorCode(resp.error.code);
          setErrorMessage(resp.error.message);
          setLoading(false);
        } else {
          const { roomId, inviteeEmail } = resp.invite!;

          // Track accepting an invite
          const studioIdStr = toHexFromId(roomId);
          if (studioIdStr && inviteeEmail) {
            track(EVENT_NAMES.COLLAB_ACCEPT_INVITE, EVENT_CATEGORIES.COLLABORATION, {
              studioId: studioIdStr,
              inviteeEmail
            });
          } else {
            track(EVENT_NAMES.COLLAB_ACCEPT_INVITE, EVENT_CATEGORIES.COLLABORATION);
          }

          if (config.vendor === 'ptzoptics') {
            const organizationData = extractOrganizationData(auth0User!);

            // Use organization name from `auth0User` if available, otherwise fall back to Auth0 user name
            const organizationName = organizationData?.name;
            const fallbackName = organizationName ?? auth0User?.name ?? auth0User?.email;

            // Ensure the invitee also has their own default organization and room
            await ensureDefaultOrgAndRoom(token, fallbackName, organizationName);
          }

          window.location.href = Paths.room.resolve(toHexFromId(roomId)!);
        }
      } catch (error) {
        setLoading(false);
        setErrorCode(500);
        setErrorMessage(error instanceof Error ? error.message : undefined);
        console.error(`Failed to redeem invite: ${error}`);
      }
    };

    void fn();
  }, []);

  useRenderedBootHumanWait(!loading && errorCode !== undefined);

  return (
    <div>
      <div className='relative z-10 flex justify-end p-16'>
        <Account />
      </div>
      <Centered className='backdrop-blur-sm'>
        {loading || errorCode === undefined ? (
          <div>
            <Loader delay={{ delayMillis: 0 }} />
          </div>
        ) : (
          <div
            className={twMerge(
              clsx(
                'rounded-12 border-t-12 border-primary bg-light max-h-[650px] w-[450px] p-32 pt-12 text-black',
                {
                  'border-error': !!errorCode,
                  'border-primary': !errorCode
                }
              )
            )}
          >
            <InviteError
              code={errorCode}
              inviteeObscuredEmail={inviteeObscuredEmail}
              errorMessage={errorMessage}
            />
          </div>
        )}
      </Centered>
    </div>
  );
};
